import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import './InquiryForm.css';

const InquiryForm = ({ onSuccess }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    itineraryId: '',
    travelMonth: '',
    numberOfGuests: '2',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Map destination ID to name
      const destinationNames = {
        'alaska': 'Alaska',
        'caribbean': 'Caribbean',
        'mediterranean': 'Mediterranean',
        'other': 'Other / Not Sure Yet'
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/inquiries/`, {
        ...formData,
        itineraryName: destinationNames[formData.itineraryId] || formData.itineraryId
      });

      if (response.data) {
        toast({
          title: 'Inquiry Submitted!',
          description: 'Thank you for your interest. We will contact you shortly.',
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          itineraryId: '',
          travelMonth: '',
          numberOfGuests: '2',
          message: ''
        });
        
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit inquiry. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="inquiry-form">
      <div className="form-group">
        <Label htmlFor="name">Full Name *</Label>
        <Input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
          placeholder="Your full name"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
            placeholder="your@email.com"
          />
        </div>

        <div className="form-group">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            required
            placeholder="+91 9820110644"
          />
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="destination">Preferred Destination</Label>
        <Select value={formData.itineraryId} onValueChange={(value) => handleChange('itineraryId', value)}>
          <SelectTrigger id="destination">
            <SelectValue placeholder="Select a destination" />
          </SelectTrigger>
          <SelectContent position="popper" className="max-h-[300px] z-[10001]">
            <SelectItem value="alaska">Alaska</SelectItem>
            <SelectItem value="caribbean">Caribbean</SelectItem>
            <SelectItem value="mediterranean">Mediterranean</SelectItem>
            <SelectItem value="other">Other / Not Sure Yet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <Label htmlFor="travelMonth">Preferred Month of Travel</Label>
          <Select value={formData.travelMonth} onValueChange={(value) => handleChange('travelMonth', value)}>
            <SelectTrigger id="travelMonth">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent position="popper" className="max-h-[300px] z-[10001]">
              <SelectItem value="January 2026">January 2026</SelectItem>
              <SelectItem value="February 2026">February 2026</SelectItem>
              <SelectItem value="March 2026">March 2026</SelectItem>
              <SelectItem value="April 2026">April 2026</SelectItem>
              <SelectItem value="May 2026">May 2026</SelectItem>
              <SelectItem value="June 2026">June 2026</SelectItem>
              <SelectItem value="July 2026">July 2026</SelectItem>
              <SelectItem value="August 2026">August 2026</SelectItem>
              <SelectItem value="September 2026">September 2026</SelectItem>
              <SelectItem value="October 2026">October 2026</SelectItem>
              <SelectItem value="November 2026">November 2026</SelectItem>
              <SelectItem value="December 2026">December 2026</SelectItem>
              <SelectItem value="January 2027">January 2027</SelectItem>
              <SelectItem value="February 2027">February 2027</SelectItem>
              <SelectItem value="March 2027">March 2027</SelectItem>
              <SelectItem value="April 2027">April 2027</SelectItem>
              <SelectItem value="May 2027">May 2027</SelectItem>
              <SelectItem value="June 2027">June 2027</SelectItem>
              <SelectItem value="July 2027">July 2027</SelectItem>
              <SelectItem value="August 2027">August 2027</SelectItem>
              <SelectItem value="September 2027">September 2027</SelectItem>
              <SelectItem value="October 2027">October 2027</SelectItem>
              <SelectItem value="November 2027">November 2027</SelectItem>
              <SelectItem value="December 2027">December 2027</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="form-group">
          <Label htmlFor="guests">Number of Guests</Label>
          <Select value={formData.numberOfGuests} onValueChange={(value) => handleChange('numberOfGuests', value)}>
            <SelectTrigger id="guests">
              <SelectValue placeholder="Select number of guests" />
            </SelectTrigger>
            <SelectContent position="popper" className="max-h-[200px] z-[10001]">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? 'Guest' : 'Guests'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="message">Additional Details</Label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          placeholder="Tell us about your preferences, special requests, or any questions..."
          rows={4}
        />
      </div>

      <Button type="submit" className="submit-btn" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
      </Button>
    </form>
  );
};

export default InquiryForm;
